import React, { useEffect, useMemo, useRef, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { MutationStatus } from 'react-query';
import {
  ApiButton,
  Box,
  ContentTemplate,
  CustomSelect,
  HeaderInput,
  HeaderTemplate,
  MUIDataTable,
  MUIDataTableColumnDef,
  MUIDataTableOptions,
  Pill,
  PillVariant,
  SearchBar,
  SectionHeader,
  TABLE_INITIAL_STATE,
  TableActionTypes,
  TitleHeader,
  Typography,
  MUIDataTableColumn
} from 'web-library';


import { makeStyles } from 'tss-react/mui';
import { TableState, useDataTable } from 'web-library/stories/organism/DataTable/DataTable';
import { useHistory, useLocation } from 'react-router-dom';
import { stringToLongDateAndShortTime } from '../../../../utils/DateUtils';
import { usePostNonNationalExamApplicationSearch } from '../../../../api-refactored/user/usePostNonNationalExamApplicationSearch';
import { formAPIPageable, parseLocationSearch } from '../../../../utils/StringUtils';
import { ApiPathParam } from '../../../../models/SearchParamSchema';
import { decryptData, encryptData } from '../../../../utils/CryptoUtils';
import { useUserDetails } from '../../../../hooks/userDetails';
import { getIndexValue } from '../../../../utils/ArrayUtils';
import { NonNationalApplicationStatusDropdownResponse } from '../../../../api-refactored/non-national-exam-registration/useGetNonNationalApplicationStatusDropdown';

const useStyles = makeStyles()((theme) =>
({
  select: {
    marginBottom: theme.spacing(3),
  },
  searchContainer: {
    paddingBottom: theme.spacing(2),
  },
  searchInput: {
    maxWidth: '100%',
  },
  subtitleText: {
    color: theme.color.text.textDisabled,
  },
}));

interface ITableItemData {
  uuid: string;
  nricFinPassport: string;
  cddtName: string;
  applicationDate: string;
  status: string;
  updateDateTime: string;
  updateBy: string;
  applicationId: string;
}

interface TableColumnProps {
  applicationList: ITableItemData[];
  classes: Record<string,string>;
  onNavigate: (uuid: string, status: string) => void;
  t: TFunction;
  allApplicationStatusDropdown: NonNationalApplicationStatusDropdownResponse[];
  apiGetApplicationStatus: MutationStatus;
}

export interface ExamRegistrationRequestParam extends ApiPathParam {
  examLevel: string;
  status: string[];
  search: string;
}

const renderStatusList = (data: NonNationalApplicationStatusDropdownResponse[]) =>
  data.map((item) => ({
    children: item.description || '-',
    key: item.description || '-',
    value: item.description || '-',
  }));

const tableColumns = ({
  classes,
  t,
  applicationList,
  onNavigate,
  allApplicationStatusDropdown,
  apiGetApplicationStatus,
}: TableColumnProps): MUIDataTableColumnDef[] => [
    {
      name: 'nricFinPassport',
      label: t('exam->>ReviewExam.NricFinPassportNo'),
      options: {
        setCellProps: () => ({ style: { width: '15%' } }),
        filter: false,
        sort: false,
      },
    },
    {
      name: 'cddtName',
      label: t('exam->>ReviewExam.CandidateName'),
      options: {
        setCellProps: () => ({ style: { width: '30%' } }),
        filter: false,
        sort: false,
      },
    },
    {
      name: 'applicationDateTime',
      label: t('exam->>RegistrationSummary.ApplicationDateTime'),
      options: {
        sort: true,
        filter: false,
        setCellProps: () => ({ style: { width: '15%' } }),
        customBodyRenderLite: (dataIndex: number) => (
          <>
            <Typography variant="body2">
              {applicationList[dataIndex].applicationDate
                ? stringToLongDateAndShortTime(applicationList[dataIndex].applicationDate)
                : '-'}
            </Typography>
            <Typography variant="subtitle2" className={classes.subtitleText}>
              {applicationList[dataIndex].applicationId ?? '-'}
            </Typography>
          </>
        ),
      },
    },
    {
      name: 'status',
      label: t('exam->>RegistrationSummary.Status'),
      options: {
        sort: false,
        filter: true,
        filterType: 'custom',
        filterOptions: {
          fullWidth: true,
          display: (filterList: string[][], onChange: (val: string | string[], index: number, column: MUIDataTableColumn) => void, index: number, column: MUIDataTableColumn) => (
            <CustomSelect
              className={classes.select}
              // eslint-disable-next-line react/destructuring-assignment
              value={filterList[index][0]}
              displayEmpty
              menuItems={renderStatusList(allApplicationStatusDropdown)}
              onChange={(event) => {
                // eslint-disable-next-line no-param-reassign
                filterList[index][0] = event.target.value as string;
                onChange(filterList[index], index, column);
              }}
              label={t('admin->>TransactionLogList.Status')}
              labelId="inputSelectLabel-status"
              id="inputSelect-status"
            />
          ),
        },
        setCellProps: () => ({ style: { width: '10%' } }),
        customBodyRenderLite: (dataIndex: number) => {
          const value = applicationList[dataIndex].status;
          let variant: PillVariant;

          switch (value) {
            case 'Pending SEAB Review':
            case 'Pending MOE Review':
            case 'Incomplete (Resubmission Required)':
            case 'Pending MOE Review (Withdrawal)':
            case 'Refund Approved':
              variant = 'info';
              break;

            case 'Registered':
            case 'Withdrawn (Refunded)':
              variant = 'positive';
              break;

            case 'Ineligible':
            case 'Withdrawn (No Refund)':
              variant = 'negative';
              break;

            default:
              variant = 'default';
              break;
          }
          return <Pill variant={variant} label={value || '-'} />;
        },
      },
    },
    {
      name: 'updateDateTime',
      label: t('exam->>NonNationalExam.DateTimeUpdated'),
      options: {
        setCellProps: () => ({ style: { width: '15%' } }),
        filter: false,
        sort: true,
        customBodyRenderLite: (dataIndex: number) => {
          const dateValue = applicationList[dataIndex].updateDateTime;
          const updaterValue = applicationList[dataIndex].updateBy;
          return (
            <>
              <Typography variant="body2">
                {dateValue ? stringToLongDateAndShortTime(dateValue) : '-'}
              </Typography>
              <Typography variant="subtitle2" className={classes.subtitleText}>
                {updaterValue ? `Updated by ${updaterValue}` : '-'}
              </Typography>
            </>
          );
        },
      },
    },
    {
      name: 'action',
      label: t('exam->>SelectSubjects.Action'),
      options: {
        setCellProps: () => ({ style: { width: '15%' } }),
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex: number) => (
          <ApiButton
            status={apiGetApplicationStatus}
            type="button"
            onClick={() =>
              onNavigate(applicationList[dataIndex].uuid, applicationList[dataIndex].status)
            }
            variant="text"
            color="primary"
            style={{ textDecoration: 'none', padding: 0, height: 'auto' }}
          >
            <Typography variant="h5">{t('exam->>NonNationalExam.ReviewApplication')}</Typography>
          </ApiButton>
        ),
      },
    },
  ];

const getFiltersMap = (status: string | undefined) => {
  const filterMap: {
    [key: string]: any;
  } = {};

  if (status) {
    filterMap.paymentMode = status.replace('[', '').replace(']', '').split(',');
  }

  return filterMap;
};

const useGetRequestParam = (csrfToken: string | undefined) => {
  const location = useLocation<ExamRegistrationRequestParam>();
  const requestParam = parseLocationSearch(location.search);
  const filterMap = getFiltersMap(requestParam.status);
  const sortColumn = requestParam.sort?.split(',')[0];
  const order = requestParam.sort?.split(',')[1] === 'asc' ? 'asc' : 'desc';
  let decryptQ = '';

  if (requestParam.search) {
    decryptQ = decryptData(decodeURIComponent(requestParam.search || ''), csrfToken);
  }

  return {
    filterMap,
    page: requestParam.page,
    size: requestParam.size,
    search: decryptQ,
    examLevel: requestParam.examLevel,
    sort: sortColumn,
    order,
  };
};

const formSearchQuery = (state: TableState, csrfToken: string, selectedExamType: string) => {
  let path = formAPIPageable({
    order: state.order,
    sort: state.sortColumn,
    page: state.page,
    pageSize: state.size,
  });
  if (path !== '?') {
    path += '&';
  }

  if (selectedExamType) {
    path += `examLevel=${selectedExamType}&`;
  }

  if (getIndexValue(state.filters.status, 0)) {
    path += `status=${getIndexValue(state.filters.status, 0)}&`;
  }

  if (state.search) {
    const searchField = encodeURIComponent(encryptData(state.search, csrfToken));
    path += `search=${searchField}&`;
  }

  if (path.length) {
    path = path.substring(0, path.length - 1);
  }

  return path;
};

export const usePrevious = (value: any) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value; // assign the value of ref to the argument
  }, [value]); // this code will run when the value of 'value' changes
  return ref.current; // in the end, return the current ref value.
};

const ExamRegistrationSearchAndFilter = ({
  isMobile,
  selectedExamTypeTitle,
  examTypeFunctionCode,
  onNavigate,
  selectedExamType,
  setSelectedExamType,
  allApplicationStatusDropdown,
  apiGetApplicationStatus,
}: {
  isMobile: boolean;
  examTypeFunctionCode: any;
  selectedExamTypeTitle: string;
  onNavigate: (uuid: string, status: string) => void;
  selectedExamType: string;
  setSelectedExamType: React.Dispatch<React.SetStateAction<string>>;
  allApplicationStatusDropdown: NonNationalApplicationStatusDropdownResponse[];
  apiGetApplicationStatus: MutationStatus;
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [helperText, setHelperText] = useState<string>();
  const [searchError, setSearchError] = useState<boolean>(false);
  const history = useHistory();
  const location = useLocation();
  const prevSelectedExamType = usePrevious(selectedExamType);

  const { data: userDetailData } = useUserDetails();
  const csrfToken = userDetailData?.data?.csrfToken || '';
  const requestParam = useGetRequestParam(csrfToken);

  const { state, dispatch: tableDispatch, tOptions } = useDataTable({
    ...TABLE_INITIAL_STATE,
    sortColumn: requestParam.sort || 'updateDateTime',
    order: requestParam.order === 'asc' ? 'asc' : 'desc',
    size: requestParam.size !== undefined ? parseInt(requestParam.size, 10) : 5,
    page: requestParam.page !== undefined ? parseInt(requestParam.page, 10) : 0,
    search: requestParam.search || '',
    filters: requestParam.filterMap,
  });

  const { mutate, isLoading } = usePostNonNationalExamApplicationSearch({
    onSuccess: (data) => {
      if (data) {
        tableDispatch({
          type: 'UPDATE_DATA',
          payload: {
            data: data.content,
            count: data.totalElements,
          },
        } as TableActionTypes);

        history.replace(
          `${location.pathname}${formSearchQuery(state, csrfToken, selectedExamType)}`
        );
      }
    },
  });

  const onSearch = (candidateFilter: string) => {
    tableDispatch({
      type: 'UPDATE_SEARCH',
      payload: candidateFilter.trim(),
    });
    setHelperText(undefined);
    setSearchError(false);
  };

  useEffect(() => {
    if (requestParam.examLevel) {
      setSelectedExamType(requestParam.examLevel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestParam.examLevel]);

  useEffect(() => {
    if (prevSelectedExamType && selectedExamType) {
      mutate({
        search: state.search,
        apiParams: {
          pageSize: state.size,
          page: state.page,
          order: state.order,
          sort: state.sortColumn,
        },
        examLevel: selectedExamType,
        status: state.filters.status || [],
      });
    }
  }, [
    state.search,
    state.order,
    state.sortColumn,
    state.page,
    state.size,
    state.filters.status,
    mutate,
    selectedExamType,
    prevSelectedExamType,
  ]);

  const appendedServerSizePaginationColumnOption = useMemo<MUIDataTableOptions>(
    () => ({
      filter: true,
      filterType: 'multiselect',
      pagination: true,
      sort: true,
      selectToolbarPlacement: 'none',
      sortOrder: {
        name: 'updateDateTime',
        direction: requestParam.order === 'asc' ? 'asc' : 'desc',
      },
    }),
    [requestParam.order]
  );

  return (
    <>
      <TitleHeader title={t('exam->>NonNationalExam.ApplicationsReview')} />
      <HeaderTemplate>
        <HeaderInput
          label={t('exam->>SelectSubjects.ExamLevel')}
          topBottom={isMobile}
          input={
            <CustomSelect
              id="inputSelect-examType"
              isHeader
              displayEmpty
              onChange={(event) => {
                setSelectedExamType(event.target.value as string);

                tableDispatch({
                  type: 'UPDATE_PAGE',
                  payload: 0,
                });
                tableDispatch({
                  type: 'UPDATE_SIZE',
                  payload: 5,
                });
                tableDispatch({
                  type: 'UPDATE_SEARCH',
                  payload: '',
                });
                tableDispatch({
                  type: 'UPDATE_FILTERS',
                  payload: [],
                });
                tableDispatch({
                  type: 'UPDATE_SORT',
                  payload: {
                    sortColumn: 'updateDateTime',
                    order: 'desc',
                  },
                });
              }}
              menuItems={examTypeFunctionCode}
              variant="filled"
              placeholder={t('exam->>SelectSubjects.ExamLevel')}
              value={selectedExamType}
            />
          }
        />
      </HeaderTemplate>
      <ContentTemplate type="full">
        <SectionHeader
          title={`${selectedExamTypeTitle} ${t(
            'exam->>NonNationalExam.ExamRegistrationApplication'
          )}`}
        />
        <Box className={classes.searchContainer}>
          <SearchBar
            className={classes.searchInput}
            placeholder={t('admin->>RegistrationReport.SearchPlaceholder')}
            name="searchInput"
            ariaLabel="searchInput"
            defaultValue={state.search}
            helperText={helperText}
            error={searchError}
            onSearch={(str: string) => {
              onSearch(str);
            }}
          />
        </Box>
        <Box>
          <MUIDataTable
            state={state}
            isLoading={isLoading}
            dispatch={tableDispatch}
            columns={tableColumns({
              classes,
              t,
              applicationList: state.data,
              onNavigate,
              allApplicationStatusDropdown,
              apiGetApplicationStatus,
            })}
            data={state.data}
            options={{ ...tOptions, ...appendedServerSizePaginationColumnOption }}
            rowsPerPageOptions={[5, 10, 20, 25]}
          />
        </Box>
      </ContentTemplate>
    </>
  );
};

export default ExamRegistrationSearchAndFilter;
